import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
  TextField,
  Button,
  Paper,
  Grid,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
  Alert,
  Slider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Badge,
  Tooltip,
  FormControl,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
} from '@mui/lab';
import {
  Public as EarthIcon,
  Pets as SpeciesIcon,
  Terrain as GeologyIcon,
  WbSunny as ClimateIcon,
  Science as FossilIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  History as HistoryIcon,
  ExpandMore as ExpandIcon,
  Event as EventIcon,
  Biotech as BiotechIcon,
  Nature as NatureIcon,
  Water as OceanIcon,
  Landscape as EnvironmentIcon,
} from '@mui/icons-material';
import { PaleontologyAgentService } from '../services/paleontologyAgentService';
import { 
  PaleontologySession, 
  PaleontologyAgentType,
  PALEONTOLOGY_AGENTS,
  PaleontologyAgentInfo,
  PaleontologyVisualization,
  Species,
  Fossil,
} from '../models/paleontologyAgent';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
};

export const PaleontologyAgent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [activeTab, setActiveTab] = useState(0);
  const [selectedAgent, setSelectedAgent] = useState<PaleontologyAgentType>('mesozoic');
  const [sessions, setSessions] = useState<PaleontologySession[]>([]);
  const [currentSession, setCurrentSession] = useState<PaleontologySession | null>(null);
  const [initialPrompt, setInitialPrompt] = useState('');
  const [detailDomain, setDetailDomain] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(2048);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = () => {
    const allSessions = PaleontologyAgentService.getAllSessions();
    setSessions(allSessions);
  };

  const getAgentInfo = (agentType: PaleontologyAgentType): PaleontologyAgentInfo | undefined => {
    return PALEONTOLOGY_AGENTS.find(a => a.id === agentType);
  };

  const handleStartSession = async () => {
    if (!initialPrompt.trim()) {
      setError('Please enter an initial prompt');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await PaleontologyAgentService.startSession(selectedAgent, {
        initialPrompt,
        temperature,
        maxTokens,
      });

      if (response.success && response.sessionId) {
        const session = PaleontologyAgentService.createLocalSession(
          selectedAgent,
          response.sessionId,
          initialPrompt,
          response.visualization
        );

        setCurrentSession(session);
        loadSessions();
        setInitialPrompt('');
        setActiveTab(1); // Switch to visualization tab
      } else {
        setError(response.errors?.join(', ') || 'Failed to start session');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start session');
    } finally {
      setLoading(false);
    }
  };

  const handleIterate = async () => {
    if (!currentSession || !detailDomain.trim()) {
      setError('Please enter a detail domain or aspect to explore');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const agentInfo = getAgentInfo(currentSession.agentType);
      const response = await PaleontologyAgentService.iterate(
        currentSession.agentType,
        currentSession.sessionId,
        {
          agentType: agentInfo?.name,
          detailDomain,
          customInstructions: customInstructions || undefined,
          temperature,
          maxTokens,
        }
      );

      if (response.success && response.visualization) {
        PaleontologyAgentService.addVisualization(currentSession.id, response.visualization);
        setCurrentSession(PaleontologyAgentService.getSession(currentSession.id));
        setDetailDomain('');
        setCustomInstructions('');
      } else {
        setError(response.errors?.join(', ') || 'Failed to generate visualization');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to iterate');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSession = (session: PaleontologySession) => {
    setCurrentSession(session);
    setSelectedAgent(session.agentType);
    setHistoryOpen(false);
    setActiveTab(1);
  };

  const handleDeleteSession = (id: string) => {
    PaleontologyAgentService.deleteSession(id);
    if (currentSession?.id === id) {
      setCurrentSession(null);
      setActiveTab(0);
    }
    loadSessions();
  };

  const handleExport = () => {
    if (!currentSession) return;

    const content = PaleontologyAgentService.exportSession(currentSession);
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `paleontology-${currentSession.agentType}-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderSpecies = (species: Species, isDominant: boolean = false) => {
    return (
      <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <SpeciesIcon color={isDominant ? 'primary' : 'action'} />
          <Typography variant="subtitle1" fontWeight="bold">
            {species.name}
          </Typography>
          <Chip label={species.type} size="small" variant="outlined" />
        </Box>
        <List dense>
          {species.characteristics.map((char, i) => (
            <ListItem key={i}>
              <ListItemText primary={char} />
            </ListItem>
          ))}
        </List>
        {species.significance && (
          <Alert severity="info" sx={{ mt: 1 }}>
            <Typography variant="body2">{species.significance}</Typography>
          </Alert>
        )}
      </Box>
    );
  };

  const renderFossil = (fossil: Fossil) => {
    return (
      <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <FossilIcon />
          <Typography variant="subtitle2" fontWeight="bold">
            {fossil.name}
          </Typography>
          <Chip label={fossil.type} size="small" />
        </Box>
        {fossil.location && (
          <Typography variant="body2" color="text.secondary">
            Location: {fossil.location}
          </Typography>
        )}
        <Typography variant="body2">
          {fossil.significance}
        </Typography>
      </Box>
    );
  };

  const renderVisualization = (viz: PaleontologyVisualization, index: number) => {
    return (
      <Accordion key={index} defaultExpanded={index === 0}>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <Typography variant="h6">{viz.title}</Typography>
            <Chip label={viz.era} color="primary" />
            <Chip label={viz.period} color="secondary" />
            {viz.epoch && <Chip label={viz.epoch} variant="outlined" />}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Overview */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <EarthIcon color="primary" />
                    <Typography variant="h6">Overview</Typography>
                    <Chip label={viz.timeRange} size="small" />
                  </Box>
                  <Typography paragraph>{viz.description}</Typography>
                  <Typography>{viz.content.overview}</Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Key Features */}
            {viz.keyFeatures.length > 0 && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Key Features</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {viz.keyFeatures.map((feature, i) => (
                        <Chip key={i} label={feature} color="primary" variant="outlined" />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Geological Context */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <GeologyIcon />
                    <Typography variant="h6">Geological Context</Typography>
                  </Box>
                  <Typography paragraph>
                    <strong>Tectonic Activity:</strong> {viz.content.geologicalContext.tectonicActivity}
                  </Typography>
                  <Typography paragraph>
                    <strong>Continental Configuration:</strong> {viz.content.geologicalContext.continentalConfiguration}
                  </Typography>
                  {viz.content.geologicalContext.rockFormations.length > 0 && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>Rock Formations:</Typography>
                      <List dense>
                        {viz.content.geologicalContext.rockFormations.map((rock, i) => (
                          <ListItem key={i}>
                            <ListItemText primary={rock} />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Environmental Conditions */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <ClimateIcon />
                    <Typography variant="h6">Environmental Conditions</Typography>
                  </Box>
                  <Typography paragraph>
                    <strong>Climate:</strong> {viz.content.environment.climate}
                  </Typography>
                  <Typography paragraph>
                    <strong>Atmosphere:</strong> {viz.content.environment.atmosphere}
                  </Typography>
                  {viz.content.environment.oceanConditions && (
                    <Typography paragraph>
                      <strong>Ocean Conditions:</strong> {viz.content.environment.oceanConditions}
                    </Typography>
                  )}
                  {viz.content.environment.vegetationType && (
                    <Typography paragraph>
                      <strong>Vegetation:</strong> {viz.content.environment.vegetationType}
                    </Typography>
                  )}
                  {viz.content.environment.ecosystems.length > 0 && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>Ecosystems:</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {viz.content.environment.ecosystems.map((eco, i) => (
                          <Chip key={i} label={eco} size="small" />
                        ))}
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Lifeforms */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <NatureIcon />
                    <Typography variant="h6">Lifeforms</Typography>
                  </Box>

                  {viz.content.lifeforms.dominantSpecies.length > 0 && (
                    <>
                      <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                        Dominant Species
                      </Typography>
                      {viz.content.lifeforms.dominantSpecies.map((species, i) => (
                        <div key={i}>{renderSpecies(species, true)}</div>
                      ))}
                    </>
                  )}

                  {viz.content.lifeforms.emergingSpecies.length > 0 && (
                    <>
                      <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                        Emerging Species
                      </Typography>
                      {viz.content.lifeforms.emergingSpecies.map((species, i) => (
                        <div key={i}>{renderSpecies(species, false)}</div>
                      ))}
                    </>
                  )}

                  {viz.content.lifeforms.evolutionaryInnovations.length > 0 && (
                    <>
                      <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                        Evolutionary Innovations
                      </Typography>
                      <List>
                        {viz.content.lifeforms.evolutionaryInnovations.map((innovation, i) => (
                          <ListItem key={i}>
                            <ListItemText primary={innovation} />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Fossil Record */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <FossilIcon />
                    <Typography variant="h6">Fossil Record</Typography>
                  </Box>

                  {viz.content.fossilRecord.notableFossils.length > 0 && (
                    <>
                      <Typography variant="subtitle1" gutterBottom>Notable Fossils</Typography>
                      {viz.content.fossilRecord.notableFossils.map((fossil, i) => (
                        <div key={i}>{renderFossil(fossil)}</div>
                      ))}
                    </>
                  )}

                  {viz.content.fossilRecord.preservationTypes.length > 0 && (
                    <>
                      <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                        Preservation Types
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {viz.content.fossilRecord.preservationTypes.map((type, i) => (
                          <Chip key={i} label={type} variant="outlined" />
                        ))}
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Major Events Timeline */}
            {viz.content.majorEvents.length > 0 && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <EventIcon />
                      <Typography variant="h6">Major Events</Typography>
                    </Box>
                    <Timeline position="alternate">
                      {viz.content.majorEvents.map((event, i) => (
                        <TimelineItem key={i}>
                          <TimelineSeparator>
                            <TimelineDot color="primary" />
                            {i < viz.content.majorEvents.length - 1 && <TimelineConnector />}
                          </TimelineSeparator>
                          <TimelineContent>
                            <Typography>{event}</Typography>
                          </TimelineContent>
                        </TimelineItem>
                      ))}
                    </Timeline>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Significance */}
            {viz.content.significance.length > 0 && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Significance</Typography>
                    <List>
                      {viz.content.significance.map((sig, i) => (
                        <ListItem key={i}>
                          <ListItemText primary={sig} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Modern Connections */}
            {viz.content.modernConnections && viz.content.modernConnections.length > 0 && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Modern Connections</Typography>
                    <List>
                      {viz.content.modernConnections.map((connection, i) => (
                        <ListItem key={i}>
                          <ListItemText primary={connection} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: 1 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 0.5, sm: 1 },
              fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' }
            }}
          >
            <BiotechIcon sx={{ fontSize: { xs: 32, sm: 40 } }} /> 
            {isMobile ? 'Paleo Machine' : 'Paleontology Time Machine'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton 
              onClick={() => setHistoryOpen(true)} 
              title={isMobile ? 'History' : 'Session History'}
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <Badge badgeContent={sessions.length} color="primary">
                <HistoryIcon />
              </Badge>
            </IconButton>
            {currentSession && (
              <IconButton 
                onClick={handleExport} 
                title={isMobile ? 'Export' : 'Export Analysis'}
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <DownloadIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' }
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Tabs 
          value={activeTab} 
          onChange={(_, val) => setActiveTab(val)} 
          sx={{ 
            mb: { xs: 1.5, sm: 2 },
            '& .MuiTab-root': {
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              minHeight: 48
            }
          }}
          variant={isMobile ? 'fullWidth' : 'standard'}
        >
          <Tab label={isMobile ? 'Select Era' : 'Select Era & Start'} />
          <Tab label={isMobile ? 'Results' : 'Time Travel Results'} disabled={!currentSession} />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12}>
              <Typography 
                variant="h5" 
                gutterBottom 
                sx={{ 
                  mb: { xs: 2, sm: 3 },
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }}
              >
                {isMobile ? 'Choose Era' : 'Choose Your Time Period'}
              </Typography>
              
              <Grid container spacing={2}>
                {PALEONTOLOGY_AGENTS.map((agent) => (
                  <Grid item xs={12} sm={6} md={4} key={agent.id}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        border: selectedAgent === agent.id ? 3 : 0,
                        borderColor: 'primary.main',
                        '&:hover': { 
                          bgcolor: 'action.hover',
                          transform: 'scale(1.02)',
                          transition: 'transform 0.2s'
                        }
                      }}
                      onClick={() => setSelectedAgent(agent.id)}
                    >
                      <CardContent sx={{ p: { xs: 2, sm: 2, md: 3 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, mb: 1 }}>
                          <Typography variant="h3" sx={{ fontSize: { xs: '2rem', sm: '3rem' } }}>
                            {agent.icon}
                          </Typography>
                          <Box>
                            <Typography 
                              variant="h6"
                              sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                            >
                              {agent.name}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                            >
                              {agent.timeRange}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          paragraph
                          sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                        >
                          {agent.description}
                        </Typography>
                        <Box sx={{ mb: 1 }}>
                          <Typography 
                            variant="caption" 
                            fontWeight="bold"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          >
                            {isMobile ? 'Periods:' : 'Key Periods:'}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {agent.keyPeriods.map((period, i) => (
                              <Chip 
                                key={i} 
                                label={period} 
                                size="small" 
                                variant="outlined"
                                sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}
                              />
                            ))}
                          </Box>
                        </Box>
                        <Box>
                          <Typography 
                            variant="caption" 
                            fontWeight="bold"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          >
                            {isMobile ? 'Events:' : 'Major Events:'}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {agent.majorEvents.slice(0, isMobile ? 2 : 3).map((event, i) => (
                              <Chip 
                                key={i} 
                                label={event} 
                                size="small"
                                sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}
                              />
                            ))}
                            {agent.majorEvents.length > (isMobile ? 2 : 3) && (
                              <Chip 
                                label={`+${agent.majorEvents.length - (isMobile ? 2 : 3)}`} 
                                size="small"
                                sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}
                              />
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12} md={8}>
              <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                >
                  {isMobile ? 'Start Journey' : 'Start Your Journey Through Time'}
                </Typography>

                <Alert 
                  severity="info" 
                  sx={{ 
                    mb: 2,
                    fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                  }}
                >
                  {isMobile ? 'Era: ' : 'Selected Era: '}<strong>{getAgentInfo(selectedAgent)?.name}</strong>
                </Alert>

                <TextField
                  fullWidth
                  multiline
                  rows={isMobile ? 3 : 4}
                  label={isMobile ? 'Explore what?' : 'What would you like to explore?'}
                  value={initialPrompt}
                  onChange={(e) => setInitialPrompt(e.target.value)}
                  placeholder={isMobile ? 'E.g., Dominant predators...' : 'E.g., Tell me about the dominant predators of this era, or explore the climate conditions...'}
                  sx={{ mb: 2 }}
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />

                <Button
                  variant="contained"
                  fullWidth
                  size={isMobile ? 'medium' : 'large'}
                  onClick={handleStartSession}
                  disabled={!initialPrompt.trim() || loading}
                  startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <PlayIcon />)}
                  sx={{ 
                    minHeight: 44,
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                  }}
                >
                  {loading ? (isMobile ? 'Traveling...' : 'Traveling Through Time...') : (isMobile ? 'Begin Travel' : 'Begin Time Travel')}
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                >
                  {isMobile ? 'Settings' : 'Model Settings'}
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <Typography 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    Temperature: {temperature}
                  </Typography>
                  <Slider
                    value={temperature}
                    onChange={(_, val) => setTemperature(val as number)}
                    min={0}
                    max={1}
                    step={0.1}
                    marks
                    valueLabelDisplay="auto"
                  />
                </FormControl>

                <FormControl fullWidth>
                  <Typography 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {isMobile ? 'Tokens' : 'Max Tokens'}: {maxTokens}
                  </Typography>
                  <Slider
                    value={maxTokens}
                    onChange={(_, val) => setMaxTokens(val as number)}
                    min={512}
                    max={4096}
                    step={256}
                    marks
                    valueLabelDisplay="auto"
                  />
                </FormControl>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {currentSession && (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: { xs: 2, sm: 3 }, position: { xs: 'static', md: 'sticky' }, top: 20 }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                  >
                    {isMobile ? 'Explore' : 'Explore Further'}
                  </Typography>

                  <Alert 
                    severity="success" 
                    sx={{ 
                      mb: 2,
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                    }}
                  >
                    {isMobile ? '' : 'Era: '}{getAgentInfo(currentSession.agentType)?.name}
                  </Alert>

                  <TextField
                    fullWidth
                    multiline
                    rows={isMobile ? 3 : 4}
                    label={isMobile ? 'Explore what?' : 'What aspect would you like to explore?'}
                    value={detailDomain}
                    onChange={(e) => setDetailDomain(e.target.value)}
                    placeholder={isMobile ? 'Species, events...' : 'E.g., specific species, geological events, climate changes, extinction events...'}
                    sx={{ mb: 2 }}
                    InputProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                    InputLabelProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                  />

                  <TextField
                    fullWidth
                    multiline
                    rows={isMobile ? 2 : 3}
                    label={isMobile ? 'Instructions' : 'Additional Instructions (optional)'}
                    value={customInstructions}
                    onChange={(e) => setCustomInstructions(e.target.value)}
                    placeholder={isMobile ? 'Focus...' : 'Add any specific focus or requirements...'}
                    sx={{ mb: 2 }}
                    InputProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                    InputLabelProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                  />

                  <Button
                    variant="contained"
                    fullWidth
                    size={isMobile ? 'medium' : 'large'}
                    onClick={handleIterate}
                    disabled={!detailDomain || loading}
                    startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <PlayIcon />)}
                    sx={{ 
                      minHeight: 44,
                      fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                    }}
                  >
                    {loading ? 'Exploring...' : (isMobile ? 'Continue' : 'Continue Exploration')}
                  </Button>

                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{ 
                      mt: 2,
                      minHeight: 44,
                      fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                    }}
                    onClick={() => {
                      setCurrentSession(null);
                      setActiveTab(0);
                    }}
                  >
                    {isMobile ? 'New Journey' : 'Start New Journey'}
                  </Button>
                </Paper>
              </Grid>

              <Grid item xs={12} md={8}>
                {currentSession.visualizations.length === 0 ? (
                  <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                    <EarthIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: 'text.secondary', mb: { xs: 1.5, sm: 2 } }} />
                    <Typography 
                      variant="h6" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                    >
                      Your time travel analysis will appear here
                    </Typography>
                  </Paper>
                ) : (
                  <Box>
                    <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                      Time Travel Analysis
                    </Typography>
                    {currentSession.visualizations.map((viz, index) => 
                      renderVisualization(viz, index)
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </TabPanel>

        {/* Session History Dialog */}
        <Dialog 
          open={historyOpen} 
          onClose={() => setHistoryOpen(false)} 
          maxWidth="md" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
            {isMobile ? 'Sessions' : 'Paleontology Sessions'}
          </DialogTitle>
          <DialogContent>
            <List>
              {sessions.map((session) => (
                <React.Fragment key={session.id}>
                  <ListItem sx={{ py: { xs: 1.5, sm: 2 } }}>
                    <ListItemText
                      primary={`${getAgentInfo(session.agentType)?.name} - ${session.initialPrompt.slice(0, isMobile ? 30 : 50)}...`}
                      secondary={`${isMobile ? '' : 'Created: '}${new Date(session.createdAt).toLocaleString()} | ${session.visualizations.length} ${isMobile ? 'anal.' : 'analyses'}`}
                      primaryTypographyProps={{
                        sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                      }}
                      secondaryTypographyProps={{
                        sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                      }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        onClick={() => handleLoadSession(session)}
                        sx={{ minWidth: 44, minHeight: 44 }}
                        title={isMobile ? 'Load' : 'Load session'}
                      >
                        <PlayIcon />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDeleteSession(session.id)}
                        sx={{ minWidth: 44, minHeight: 44 }}
                        title={isMobile ? 'Delete' : 'Delete session'}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 2 } }}>
            <Button 
              onClick={() => setHistoryOpen(false)}
              sx={{ 
                minHeight: 44,
                fontSize: { xs: '0.875rem', sm: '0.9375rem' }
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};